import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import {
  ActivatedRoute,
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationStart,
  Router,
  RouterOutlet
} from '@angular/router';

import { AuthService } from '@core/services/auth.service';
import { DevAuthService } from '@core/services/dev-auth.service';

import { AppLayoutComponent, NavSection } from './shared/components/app-layout/app-layout.component';

/**
 * Root component — toggles between the authenticated shell (AppLayoutComponent)
 * and the bare outlet for public pages (login, password flows, 404).
 *
 * Nav sections follow plan §11: Dashboard / Carts / Items / Payouts / Profile
 * are primary tabs, Disputes is a secondary entry.
 */
@Component({
  selector: 'app-root',
  standalone: true,
  imports: [MatProgressBarModule, RouterOutlet, AppLayoutComponent],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AppComponent implements OnInit {
  private readonly authService = inject(AuthService);
  private readonly devAuth = inject(DevAuthService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly destroyRef = inject(DestroyRef);

  readonly showShell = signal(false);
  readonly navigating = signal(false);
  readonly sections: NavSection[] = [
    {
      title: '',
      items: [
        { label: 'Dashboard', icon: 'home', route: '/dashboard' },
        { label: 'Drop-off Carts', icon: 'package', route: '/carts' },
        { label: 'My Items', icon: 'box', route: '/items' },
        { label: 'Payouts', icon: 'dollar', route: '/payouts' },
        { label: 'Aging Price Requests', icon: 'clock', route: '/price-updates' }
      ]
    },
    {
      title: 'Support',
      items: [{ label: 'Disputes', icon: 'info', route: '/disputes' }]
    }
  ];

  ngOnInit(): void {
    this.router.events.subscribe(event => {
      if (event instanceof NavigationStart) {
        this.navigating.set(true);
      } else if (
        event instanceof NavigationEnd ||
        event instanceof NavigationCancel ||
        event instanceof NavigationError
      ) {
        this.navigating.set(false);
        if (event instanceof NavigationEnd) {
          let r = this.route.snapshot;
          while (r.firstChild) r = r.firstChild;
          this.showShell.set(!r.data['hideShell']);
        }
      }
    });

    this.authService
      .onLogoutBroadcast()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.devAuth.logout();
        this.router.navigate(['/login']);
      });
  }
}
